import api from './api';

export const TIPOS_WS = ['WSFEv1', 'WSFEXv1', 'WSMTXCA', 'WSFECred', 'WSBFEv1', 'WSSrPadronA5'] as const;
export type TipoWs = typeof TIPOS_WS[number];

export interface WsfeConfigItem {
    idwsfe: string;
    tipows: string;
    descripcion: string;
    certificado?: string;
    claveprivada?: string;
}

export interface WsfeConfigDto {
    idwsfe?: string;
    idCompania?: string;
    tipows?: string;
    descripcion?: string;
    certificado?: string;
    claveprivada?: string;
    urlautenticacion?: string;
    urlwebservices?: string;
    ta?: string;
    token?: string;
    sign?: string;
    expiration?: string;
}

export interface WsfeUploadResult {
    ruta: string;
}

export interface WsfeTestResult {
    exitoso: boolean;
    mensaje: string;
}

export interface WsfeConsultaComprobanteBase {
    idCompania: string;
    cuit: string;
    puntoVenta: number;
    codigoComprobanteAfip: number;
}

export interface WsfeUltimoAutorizadoConsultaResult {
    ultimoNumero: number | null;
}

export interface AfipConsultaComprobanteDetalle {
    exitoso: boolean;
    mensaje: string;
    resultado?: string;
    cae?: string;
    fechaComprobante?: string;
    fechaVencimientoCae?: string;
    importeTotal?: string;
    cbteDesde?: number | null;
    cbteHasta?: number | null;
}

const wsfeConfigService = {
    getWsfeConfigs: async (idCompania?: string): Promise<WsfeConfigItem[]> => {
        const params = idCompania ? { idCompania } : {};
        const response = await api.get<WsfeConfigItem[]>('/api/WsfeConfig', { params });
        return response.data || [];
    },

    getWsfeConfig: async (idwsfe: string): Promise<WsfeConfigDto> => {
        const response = await api.get<WsfeConfigDto>(`/api/WsfeConfig/${encodeURIComponent(idwsfe)}`);
        return response.data;
    },

    createWsfeConfig: async (dto: WsfeConfigDto): Promise<WsfeConfigDto> => {
        const response = await api.post<WsfeConfigDto>('/api/WsfeConfig', dto);
        return response.data;
    },

    updateWsfeConfig: async (idwsfe: string, dto: WsfeConfigDto): Promise<void> => {
        await api.put(`/api/WsfeConfig/${encodeURIComponent(idwsfe)}`, dto);
    },

    deleteWsfeConfig: async (idwsfe: string): Promise<void> => {
        await api.delete(`/api/WsfeConfig/${encodeURIComponent(idwsfe)}`);
    },

    uploadCertificado: async (idwsfe: string, idCompania: string, archivo: File): Promise<WsfeUploadResult> => {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const response = await api.post<WsfeUploadResult>(
            `/api/WsfeConfig/${encodeURIComponent(idwsfe)}/certificado`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: { idCompania },
            }
        );
        return response.data;
    },

    uploadClavePrivada: async (idwsfe: string, idCompania: string, archivo: File): Promise<WsfeUploadResult> => {
        const formData = new FormData();
        formData.append('archivo', archivo);
        const response = await api.post<WsfeUploadResult>(
            `/api/WsfeConfig/${encodeURIComponent(idwsfe)}/clave-privada`,
            formData,
            {
                headers: { 'Content-Type': 'multipart/form-data' },
                params: { idCompania },
            }
        );
        return response.data;
    },

    probarAcceso: async (idwsfe: string): Promise<WsfeTestResult> => {
        const response = await api.post<WsfeTestResult>(
            `/api/WsfeConfig/${encodeURIComponent(idwsfe)}/probar-acceso`
        );
        return response.data;
    },

    consultaUltimoAutorizado: async (
        idwsfe: string,
        body: WsfeConsultaComprobanteBase
    ): Promise<WsfeUltimoAutorizadoConsultaResult> => {
        const response = await api.post<WsfeUltimoAutorizadoConsultaResult>(
            `/api/WsfeConfig/${encodeURIComponent(idwsfe)}/consulta/ultimo-autorizado`,
            body
        );
        return response.data;
    },

    consultaComprobanteAutorizado: async (
        idwsfe: string,
        body: WsfeConsultaComprobanteBase & { numeroComprobante: number }
    ): Promise<AfipConsultaComprobanteDetalle> => {
        const response = await api.post<AfipConsultaComprobanteDetalle>(
            `/api/WsfeConfig/${encodeURIComponent(idwsfe)}/consulta/comprobante`,
            body
        );
        return response.data;
    },
};

export default wsfeConfigService;
